import { DomainList } from "@/components/domain";
import { useTheme } from "@/components/theme-provider";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
} from "@/components/ui/drawer";
import { Skeleton } from "@/components/ui/skeleton";
import { useGetClients } from "@/hooks/clients";
import { useGetAllReservationDays } from "@/hooks/reservation-days";
import { useGetAllReservationServices } from "@/hooks/reservation-services";
import { useGetReservations } from "@/hooks/reservations";
import { useGetAllServiceProviders } from "@/hooks/service-providers/get.hooks";
import { useGetServices } from "@/hooks/services";
import { useGetAllTourAgencies } from "@/hooks/tour-agencies";
import { useIsMobile } from "@/hooks/use-mobile";
import { T } from "@/i18n";
import { dateFormatedToString } from "@/lib/utils";
import {
  Reservation,
  ReservationDay,
  ReservationRegion,
  ReservationService,
} from "@/models";
import {
  CalendarEventExternal,
  CalendarType,
  createViewDay,
  createViewMonthAgenda,
  createViewMonthGrid,
  createViewWeek,
} from "@schedule-x/calendar";
import { ScheduleXCalendar, useCalendarApp } from "@schedule-x/react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { CalendarRange, Clock, EuroIcon, Eye, Pencil } from "lucide-react";
import moment from "moment";
import { useMemo, useState } from "react";
import { ScheduleList } from "../$id.index";
import {
  generateReservationLabel,
  generateReservationServiceLabel,
} from "../../-utils";
import "./index.css";

type CalendarEvent = CalendarEventExternal & {
  extras: {
    type: "reservation" | "service";
    reservation: Reservation;
    services: ReservationService[];
    service: ReservationService;
    title: string;
    days: ReservationDay[];
  };
};

export const Route = createFileRoute("/(reservations)/reservations/calendar/")({
  component: CalendarView,
});

export function CalendarView() {
  return (
    <DomainList
      domain="reservations.calendar"
      breadcrumbs={[
        {
          content: (
            <>
              <T tKey="reservations.calendar.title" />{" "}
              <CalendarRange width={16} height={16} />
            </>
          ),
          href: "/reservations/calendar",
        },
      ]}
      data={[]}
      renderItem={() => <></>}
      isLoading={false}
      noOutlet
      newLink="/reservations/calendar/new"
      type="list"
    >
      <ReservationsCalendarView />
    </DomainList>
  );
}

function ReservationsCalendarView() {
  const { data: reservations, isLoading: loadingReservations } =
    useGetReservations();
  const { data: tourAgencies, isLoading: tourAgenciesLoading } =
    useGetAllTourAgencies();
  const { data: clients, isLoading: clientsLoading } = useGetClients();

  const { data: services, isLoading: servicesLoading } =
    useGetAllReservationServices();

  const { data: days, isLoading: daysLoading } = useGetAllReservationDays();

  const { data: serviceProviders, isLoading: serviceProvidersLoading } =
    useGetAllServiceProviders();

  const { data: servicesList, isLoading: servicesListLoading } =
    useGetServices();

  const events = useMemo(() => {
    if (!reservations) return [];
    const evs: CalendarEventExternal[] = [];
    for (const r of reservations) {
      const ta = tourAgencies?.find(
        (ta) => ta.id === r.tourAgencyId?.toString(),
      );
      const groupLeader = clients?.find(
        (c) => c.id === r.groupLeaderId?.toString(),
      );
      const servicesOfReservation =
        services?.filter((s) => s.reservationId.toString() === r.id) ?? [];

      const daysOfReservation =
        days?.filter((d) => d.reservationId.toString() === r.id) ?? [];

      const title = generateReservationLabel(r, ta, groupLeader);

      const reservationEvent: CalendarEvent = {
        start: r.begin ? moment(r.begin).format("YYYY-MM-DD") : "",
        end: r.end ? moment(r.end).add(1, "day").format("YYYY-MM-DD") : r.begin ? moment(r.begin).format("YYYY-MM-DD") : "",
        id: `reservation-${r.id}`,
        title,
        calendarId: r.operationRegion?.toString() ?? "default",
        extras: {
          type: "reservation",
          services: servicesOfReservation,
          service: {} as ReservationService,
          reservation: r,
          title,
          days: daysOfReservation,
        },
      };
      evs.push(reservationEvent);
      for (const s of servicesOfReservation) {
        const serviceName = servicesList?.find(
          (sl) => sl.id === s.serviceId,
        )?.name;
        const providerName = serviceProviders?.find(
          (sp) => sp.id === s.serviceProviderId?.toString(),
        )?.provider?.name;

        const title = generateReservationServiceLabel(
          s,
          serviceName,
          providerName,
        );
        const [start, end] = getDateOfReservation(r.begin, s);
        const serviceEvent: CalendarEvent = {
          start: start ?? "",
          end: end ?? start ?? "",
          id: `service-${s.id}`,
          calendarId: `service-${r.operationRegion}`,
          title,
          extras: {
            type: "service",
            services: [],
            days: [],
            service: s,
            reservation: r,
            title,
          },
        };
          evs.push(serviceEvent);
      }
    }
    return evs;
  }, [
    clients,
    days,
    reservations,
    serviceProviders,
    services,
    servicesList,
    tourAgencies,
  ]);

  if (
    loadingReservations ||
    !reservations ||
    clientsLoading ||
    !clients ||
    servicesLoading ||
    !services ||
    serviceProvidersLoading ||
    !serviceProviders ||
    tourAgenciesLoading ||
    !tourAgencies ||
    servicesListLoading ||
    !servicesList ||
    daysLoading ||
    !days
  )
    return <Skeleton className="h-[80dvh] w-full" />;

  return <Calendar events={events} />;
}
function generateCalendar(
  key: string,
  color: string,
  textDark: boolean,
): CalendarType {
  return {
    label: key,
    colorName: key,
    lightColors: {
      main: color,
      container: color,
      onContainer: textDark ? "#000" : "#fff",
    },
    darkColors: {
      main: color,
      container: color,
      onContainer: textDark ? "#000" : "#fff",
    },
  };
}

const colorToKeys: Record<string, { color: string; dark: boolean }> = {
  //? For reservations
  [ReservationRegion.Lisbon]: { color: "#ca8a04", dark: false },
  [ReservationRegion.Faro]: { color: "#1e40af", dark: false },
  [ReservationRegion.Porto]: { color: "#14532d", dark: false },
  [ReservationRegion.Madeira]: { color: "#881337", dark: false },
  [ReservationRegion.Azores]: { color: "#115e59", dark: false },
  [ReservationRegion.Marrocos]: { color: "#9a3412", dark: false },

  //? For services
  [`service-${ReservationRegion.Lisbon}`]: { color: "#facc15", dark: true },
  [`service-${ReservationRegion.Faro}`]: { color: "#60a5fa", dark: true },
  [`service-${ReservationRegion.Porto}`]: { color: "#4ade80", dark: true },
  [`service-${ReservationRegion.Madeira}`]: { color: "#fb7185", dark: true },
  [`service-${ReservationRegion.Azores}`]: { color: "#2dd4bf", dark: true },
  [`service-${ReservationRegion.Marrocos}`]: { color: "#fb923c", dark: true },

  default: { color: "#ffffff", dark: true },
} as const;

const calendarsOfRegions = Object.keys(colorToKeys).reduce<
  Record<string, CalendarType>
>((acc, k) => {
  acc[k] = generateCalendar(k, colorToKeys[k].color, colorToKeys[k].dark);
  return acc;
}, {});

function Calendar({ events }: { events: CalendarEventExternal[] }) {
  const { isDark } = useTheme();
  const [selectedEvent, setSelectedEvent] = useState<
    CalendarEvent["extras"] | null
  >(null);
  const calendarApp = useCalendarApp({
    views: [
      createViewWeek(),
      createViewDay(),
      createViewMonthGrid(),
      createViewMonthAgenda(),
    ],

    isDark,
    theme: "shadcn",
    firstDayOfWeek: 1,
    defaultView: createViewMonthGrid().name,
    calendars: calendarsOfRegions,
    callbacks: {
      onEventClick: (ev) => {
        const { extras } = ev as CalendarEvent;
        setSelectedEvent(() => extras);
      },
    },
    selectedDate: moment().format("YYYY-MM-DD"),
    events,
  });
  return (
    <>
      <ScheduleXCalendar calendarApp={calendarApp} />
      {selectedEvent ? (
        <DetailModal
          onClose={() => {
            setSelectedEvent(null);
          }}
          {...selectedEvent}
        />
      ) : (
        <></>
      )}
    </>
  );
}

function DetailModal({
  reservation,
  service,
  services,
  days,
  type,
  onClose,
  title,
}: {
  reservation: Reservation;
  onClose: () => void;
  type: "service" | "reservation";
  services: ReservationService[];
  days: ReservationDay[];
  service: ReservationService;
  title: string;
}) {
  const isMobile = useIsMobile();

  let child: JSX.Element = <></>;

  if (type === "reservation") {
    child = (
      <ReservationSummary
        reservation={reservation}
        services={services}
        title={title}
        days={days}
      />
    );
  }

  if (type === "service") {
    child = (
      <ServiceSummary
        reservation={reservation}
        service={service}
        title={title}
      />
    );
  }

  if (isMobile)
    return (
      <Drawer open onClose={onClose}>
        <DrawerContent>
          <div className="mx-auto max-h-[90dvh] w-full max-w-lg overflow-y-auto px-4">
            <DrawerHeader></DrawerHeader>
            {child}
            <DrawerFooter></DrawerFooter>
          </div>
        </DrawerContent>
      </Drawer>
    );

  return (
    <Dialog
      open
      onOpenChange={(o) => {
        if (o) return;
        onClose();
      }}
    >
      <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-[425px]">
        <DialogHeader></DialogHeader>
        {child}
        <DialogFooter></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReservationSummary({
  reservation,
  title,
  services,
  days,
}: {
  reservation: Reservation;
  title: string;
  services: ReservationService[];
  days: ReservationDay[];
}) {
  return (
    <div className="grid gap-6">
      <div className="grid gap-2">
        <span className="text-lg font-semibold">{title}</span>
        <div className="flex items-center justify-between gap-2">
          <span className="shrink-0 text-muted-foreground">
            {dateFormatedToString(reservation.begin)} -{" "}
            {dateFormatedToString(reservation.end)}
          </span>
          <div className="flex shrink-0 items-center gap-2">
            <Button asChild size="icon" variant="text">
              <Link
                to="/reservations/calendar/$id"
                params={{ id: reservation.id }}
              >
                <Eye />
              </Link>
            </Button>
            <Button asChild size="icon" variant="text">
              <Link
                to="/reservations/calendar/$id/edit"
                params={{ id: reservation.id }}
              >
                <Pencil />
              </Link>
            </Button>
            <Button asChild size="icon" variant="text">
              <Link
                to="/reservations/calendar/$id/fulfillments"
                params={{ id: reservation.id }}
              >
                <EuroIcon />
              </Link>
            </Button>
          </div>
        </div>
      </div>
      <div className="grid gap-2">
        <span className="font-medium">
          <T tKey="reservations.form.schedule.service.title" />
        </span>
        <ScheduleList
          accommodations={[]}
          beginDate={reservation.begin}
          reservationDays={days}
          reservationId={reservation.id}
          services={services}
          hideAccommodations
        />
      </div>
    </div>
  );
}

function ServiceSummary({
  reservation,
  title,
  service,
}: {
  reservation: Reservation;
  service: ReservationService;
  title: string;
}) {
  const hour = useMemo(() => {
    if (service.startHour && service.endHour) {
      return `${service.startHour} - ${service.endHour}`;
    } else if (service.startHour) {
      return (
        <>
          {service.startHour} - <div className="w-[42.1px]" />
        </>
      );
    } else if (service.endHour) {
      return (
        <>
          <div className="w-[42.1px]" /> - {service.endHour}
        </>
      );
    }
    return (
      <>
        <div className="w-[42.1px]" /> - <div className="w-[42.1px]" />
      </>
    );
  }, [service]);
  return (
    <div className="grid gap-6">
      <div className="grid gap-1">
        <span className="text-lg font-semibold">{title}</span>
        <span className="flex items-center gap-1 text-xs text-primary">
          <Clock width={14} height={14} /> {hour}
        </span>
      </div>
      <div className="grid gap-3">
        {reservation.rwgpsrequired ? (
          <Links
            links={reservation.rwgpslinks ?? []}
            title="reservations.fields.rwgpsrequired"
          />
        ) : (
          <></>
        )}
        {reservation.travefyRequired ? (
          <Links
            links={reservation.tfylinks ?? []}
            title="reservations.fields.travefyRequired"
          />
        ) : (
          <></>
        )}
        <Links
          links={reservation.customLinks ?? []}
          title="reservations.fields.customLinks"
        />
      </div>
    </div>
  );
}

function Links({ title, links }: { title: string; links: string[] }) {
  return (
    <div className="grid gap-2">
      <span className="mb-1 font-medium">
        <T tKey={title} />
      </span>
      <div className="grid justify-items-start gap-2">
        {links.map((l, i) => (
          <Button
            asChild
            variant="text"
            size="sm"
            key={i}
            className="h-auto p-0"
          >
            <a href={l.startsWith("www") ? `https://${l}` : l}>{l}</a>
          </Button>
        ))}
      </div>
    </div>
  );
}

function getDateOfReservation(
  date: Date | null | undefined,
  rs: ReservationService,
): [string | undefined, string | undefined] {
  const gutter = !rs.startHour && !rs.endHour;
  const dtIni = date ? moment(date).add(rs.day - 1, "days") : undefined;
  const dtEnd = date ? moment(date).add(rs.day - 1, "days") : undefined;

  if (gutter) {
    return [dtIni?.format("YYYY-MM-DD"), dtEnd?.format("YYYY-MM-DD")];
  }

  const begin =
    rs.startHour ||
    rs.endHour
      .split(":")
      .map((v, i) => (i === 0 ? +v - 1 : v))
      .join(":");
  const end =
    rs.endHour ||
    rs.startHour
      .split(":")
      .map((v, i) => (i === 0 ? +v + 1 : v))
      .join(":");

  const [hs, ms] = begin.split(":");
  const [he, me] = end.split(":");

  return [
    dtIni?.set("hour", +hs).set("minutes", +ms).format("YYYY-MM-DD HH:mm"),
    dtEnd?.set("hour", +he).set("minutes", +me).format("YYYY-MM-DD HH:mm"),
  ];
}
